import { useState } from "react"
import type { ApolloError, FieldPolicy, QueryHookOptions } from "@apollo/client"
import type { PageInfo, TableQueryParams } from "./table.types"

/**
 * Apollo field policy for offset-based paginated list queries.
 * Assumes the query input has `{ limit, offset, includeDeleted, sort, filters }`
 * and the response has `{ items, pageInfo }`.
 *
 * Use in your InMemoryCache type policies to enable fetchMore pagination:
 * ```ts
 * getCustomers: paginatedField(),
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function paginatedField(): FieldPolicy<any> {
  return {
    keyArgs: ["input", ["sort", "filters", "filterTree"]],
    merge(existing, incoming, { args }) {
      const offset =
        (args as { input?: { offset?: number } } | null)?.input?.offset ?? 0
      if (offset === 0 || !existing) return incoming
      return { ...incoming, items: [...existing.items, ...incoming.items] }
    },
  }
}

type PaginatedData<TItem> = {
  items: TItem[]
  pageInfo: PageInfo | null
}

// Loose type for Apollo query hooks — only the fields we use.
// `previousData` and `error` are optional to match Apollo's QueryResult interface.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyQueryHook<TData> = (options: QueryHookOptions<TData, any>) => {
  data: TData | undefined
  previousData?: TData | undefined
  loading: boolean
  error?: ApolloError | undefined
  refetch(): void | Promise<unknown>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchMore(options: { variables: any }): Promise<unknown>
}

export type UseTableQueryOptions<TData, TItem> = {
  /** Number of items per page. Defaults to 100. */
  pageSize?: number
  /** The Apollo query hook to call (e.g. `useCustomerQuery`). */
  useQuery: AnyQueryHook<TData>
  /** Extracts `{ items, pageInfo }` from the query data. */
  select(data: TData): PaginatedData<TItem> | null | undefined
  /** Extra fields merged into the `input` GraphQL variable (e.g. `{ includeDeleted: true }`). */
  input?: Record<string, unknown>
  /**
   * Extra top-level GraphQL variables passed alongside `input`. Use
   * this for queries shaped like `(input: ListInput, scope: FooInput)`
   * where the scope is intentionally NOT mixed into `input` (so the
   * Apollo cache field policy can key on the scope independently).
   * The hook re-runs when these variables change identity, just like
   * any other Apollo variable change.
   */
  variables?: Record<string, unknown>
  /**
   * Query params for the very first fetch, before the table emits its
   * first onQueryChange. A table whose toolbar applies a default status
   * filter after mount (Table.StatusFilter with a defaultValue) passes
   * the matching params here so the initial query is already the
   * filtered one — without this, mount fires an UNFILTERED query that
   * the default pill replaces a tick later, and on big boards the
   * unfiltered query is exactly the expensive one. Keep the shape
   * identical to what buildQueryParams emits for the pill's filter, so
   * Apollo sees deep-equal variables and skips the duplicate request.
   * Read once on mount; later changes are ignored.
   */
  initialParams?: TableQueryParams
}

export type UseTableQueryResult<TItem, TData = unknown> = {
  items: TItem[]
  pageInfo: PageInfo | null
  loading: boolean
  error: ApolloError | undefined
  /**
   * Raw query data (`data ?? previousData`) so callers can pluck
   * extra fields the standard envelope doesn't carry (e.g. server-
   * computed bucket counts that live alongside `items` and
   * `pageInfo`).
   */
  data: TData | undefined
  refetch(): void
  /**
   * Loads the page after the loaded items. Takes `pageSize` items unless
   * told otherwise — the table asks for much larger pages when it fetches
   * the rest of a list for its summary row.
   */
  onFetchMore(limit?: number): void
  onQueryChange(params: TableQueryParams): void
}

/**
 * Adapter hook that connects an Apollo list query to the Table compound component.
 * Manages query params state and returns everything `Table` and `Table.Content` need.
 *
 * @example
 * ```tsx
 * const { items, pageInfo, loading, error, refetch, onFetchMore, onQueryChange } = useTableQuery({
 *   pageSize: 100,
 *   useQuery: useCustomerQuery,
 *   select: (data) => data.getCustomers,
 * })
 * ```
 */
export function useTableQuery<TData, TItem>({
  pageSize = 100,
  useQuery,
  select,
  input: extraInput = {},
  variables: extraVariables = {},
  initialParams,
}: UseTableQueryOptions<TData, TItem>): UseTableQueryResult<TItem, TData> {
  const [queryParams, setQueryParams] = useState<TableQueryParams>(
    initialParams ?? {},
  )
  const queryInput = {
    limit: pageSize,
    offset: 0,
    ...extraInput,
    ...queryParams,
  }

  const { data, previousData, loading, error, refetch, fetchMore } = useQuery({
    notifyOnNetworkStatusChange: true,
    variables: { input: queryInput, ...extraVariables },
  })

  const displayData = data ?? previousData
  const result = displayData ? select(displayData) : null
  const items = (result?.items ?? []) as TItem[]
  const pageInfo = result?.pageInfo ?? null

  return {
    items,
    pageInfo,
    loading,
    error,
    data: displayData,
    refetch,
    onQueryChange: setQueryParams,
    onFetchMore(limit = pageSize) {
      fetchMore({
        variables: {
          input: { ...queryInput, limit, offset: items.length },
          ...extraVariables,
        },
      })
    },
  }
}
